import type { SettingsPage } from "../types";

/**
 * Interview-transcript styling. Recognizes `SPEAKER: content [timestamp]` turns
 * (using the same rules yaqatw's Mining > Stats > Speaker uses) and paints the
 * speaker prefix + trailing timestamp as editable, display-only badges.
 *
 * Read reactively in src/editor/Editor.tsx (a useSettingsStore selector +
 * effect that calls syncInterviewConfig), so a change re-styles the editor
 * immediately. The master `enabled` toggle is also flipped from the View menu
 * (src/editor/MenuBar.tsx).
 */
const interviewSettings: SettingsPage = {
  id: "interview",
  label: "Interview",
  order: 3,
  items: [
    {
      key: "enabled",
      label: "Style interview transcripts",
      type: "toggle",
      default: true,
      description:
        "Recognize `SPEAKER: content [timestamp]` lines and show speaker/timestamp badges. The badges are editable and are never written to exported .docx files.",
    },
    {
      key: "detectionMode",
      label: "When to apply badges",
      type: "select",
      default: "auto",
      options: [
        { value: "auto", label: "Only when the document looks like an interview" },
        { value: "always", label: "In every document" },
      ],
      description:
        "Auto matches yaqatw's own detection: badges appear only when at least 30% of paragraphs are speaker turns, so a stray 'Note:' line in ordinary prose is not mistaken for a speaker.",
      visibleWhen: { key: "enabled", equals: true },
    },

    {
      key: "speakerHeading",
      label: "Speaker badges",
      type: "heading",
      default: "",
    },
    {
      key: "speakerColorMode",
      label: "Speaker colors",
      type: "select",
      default: "perSpeakerRole",
      options: [
        { value: "perSpeakerRole", label: "Per speaker, with an accent for interviewers" },
        { value: "perSpeaker", label: "Per speaker" },
        { value: "byRole", label: "By role (interviewer vs interviewee)" },
        { value: "uniform", label: "One color for all speakers" },
      ],
      description: "How each speaker's badge is colored.",
      visibleWhen: { key: "enabled", equals: true },
    },
    {
      key: "speakerPalette",
      label: "Color palette",
      type: "select",
      default: "vivid",
      options: [
        { value: "vivid", label: "Vivid" },
        { value: "pastel", label: "Pastel" },
        { value: "muted", label: "Muted" },
      ],
      description: "Palette used for automatic per-speaker colors (ignored for the uniform option).",
      visibleWhen: { key: "enabled", equals: true },
    },
    {
      key: "uniformSpeakerColor",
      label: "Uniform speaker color",
      type: "color",
      default: "#6366f1",
      description: "Used when 'Speaker colors' is set to a single color for all speakers.",
      visibleWhen: { key: "speakerColorMode", equals: "uniform" },
    },
    {
      key: "interviewerLabels",
      label: "Interviewer labels",
      type: "text",
      default: "INTERVIEWER",
      description:
        "Comma-separated speaker names treated as interviewers (case-insensitive), merged with the built-in INTERVIEWER, INTERVIEWER-1…5.",
      visibleWhen: { key: "enabled", equals: true },
    },

    {
      key: "timestampHeading",
      label: "Timestamp badges",
      type: "heading",
      default: "",
    },
    {
      key: "showTimestamps",
      label: "Show timestamp badges",
      type: "toggle",
      default: true,
      description: "Style a trailing [HH:MM:SS] at the end of a turn as its own badge.",
      visibleWhen: { key: "enabled", equals: true },
    },
    {
      key: "allowShortTimestamp",
      label: "Also recognize [MM:SS]",
      type: "toggle",
      default: true,
      description:
        "Recognize a shorter [MM:SS] timestamp in addition to [HH:MM:SS]. This affects the timestamp badge only — whether a document counts as an interview is always decided the same way yaqatw decides it.",
      visibleWhen: { key: "showTimestamps", equals: true },
    },
    {
      key: "timestampColor",
      label: "Timestamp badge color",
      type: "color",
      default: "#6b7280",
      description: "Background color of timestamp badges.",
      visibleWhen: { key: "showTimestamps", equals: true },
    },
  ],
};

export default interviewSettings;
